import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useSocket from "../../../hooks/useSocket";
import GalaxyLayout from "../components/layout/GalaxyLayout";
import "../../../assets/galaxy/RangBaz.css";
import axiosInstance from "../../../utils/axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Check, X } from "lucide-react";

import { setUserData } from "../../../store/userSlice";
import { useDispatch, useSelector } from "react-redux";

const BOY_GIF = "/assets/galaxy/img/LuckyJet/Boy.gif";
const LOADER_SVG = "/assets/galaxy/img/LuckyJet/loader.svg";
const CLOUDS_SVG = "/assets/galaxy/img/LuckyJet/Clouds.svg";

const STEP = 0.01;
const STEP_MS = 91; // ms per tick after 1.10 (same as AviatorX)
const SLOW_UNTIL = 1.1;
const SLOW_DURATION_MS = 2000; // 1.00 → 1.10
const SLOW_RANGE_STEPS = Math.round((SLOW_UNTIL - 1) / STEP);
const SLOW_STEP_MS = SLOW_DURATION_MS / SLOW_RANGE_STEPS;
const CLOSED_RESET_MS = 2000;
const CLIMB_DURATION = 2000;
const STAR_COUNT = 120;
const CLOUD_COUNT = 5;
const CASHOUT_MIN_MULTIPLIER = 1.1;
const SERVER_TICK_MS = 1000; // filler uses 200ms then 91ms. This is only the first gap.

const getTotalFlightSteps = (target) =>
  Math.max(1, Math.round((target - 1) / STEP) + 1);

/** Where the flight clock should stand for a given multiplier (2s slow start, then 91ms ticks). */
const elapsedMsFromMultiplier = (value) => {
  const v = Math.max(1, Number(value) || 1);
  if (v <= SLOW_UNTIL + 0.0001) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * SLOW_STEP_MS;
  }
  const extraSteps = Math.max(0, Math.round((v - SLOW_UNTIL) / STEP));
  return SLOW_DURATION_MS + extraSteps * STEP_MS;
};

const multiplierToStep = (value, target) => {
  const totalSteps = getTotalFlightSteps(target);
  const step = Math.round((value - 1) / STEP) + 1;
  return Math.min(totalSteps, Math.max(1, step));
};

function formatWinToastAmount(amount) {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toFixed(2);
}

function showWinToast(amount) {
  const display = formatWinToastAmount(amount);
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You won {display}</span>
    </div>,
    {
      toastId: "luckyjet-win-toast",
      className: "av-win-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showLostToast(amount) {
  const display = formatWinToastAmount(amount);
  toast.dismiss("luckyjet-win-toast");
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <X size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You lost {display}</span>
    </div>,
    {
      toastId: `luckyjet-lost-${Date.now()}`,
      className: "av-lost-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

export default function LuckyJetGalaxy() {
  const dispatch = useDispatch();
  const [scene, setScene] = useState("loading");
  const [launchCountdown, setLaunchCountdown] = useState(4);
  const [running, setRunning] = useState(false);
  const [flightValue, setFlightValue] = useState(0);

  const rafRef = useRef(null);
  const startTsRef = useRef(null);
  const lastStepRef = useRef(-1);
  const maxFlightRef = useRef(null);
  const sceneRef = useRef("loading");
  const closedResetRef = useRef(null);
  const lastCrashedEvtIdRef = useRef(null);
  const lastFinishedEvtIdRef = useRef(null);
  const lastFlightStartedEvtIdRef = useRef(null);
  const lastCountdownResetEvtRef = useRef(null);
  const placeAllBetsNowRef = useRef(null);
  const leftPlacingRef = useRef(false);
  const rightPlacingRef = useRef(false);
  const roundIdRef = useRef("");
  const isLoadGameRef = useRef(0);
  const flightValueRef = useRef(0);

  /** running_result is the live ceiling; the crash is only known once CLOSED publishes it */
  const lastServerRunningRef = useRef(1);
  const crashKnownRef = useRef(false);
  const counterRawRef = useRef(null);
  const capTsRef = useRef(0);
  const capFromRef = useRef(1);
  const capIntervalRef = useRef(SERVER_TICK_MS);

  /** Track disconnection so we can reset on reconnect */
  const wasDisconnectedRef = useRef(false);

  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const starsRef = useRef([]);
  const cloudsRef = useRef([]);
  const bgRafRef = useRef(null);
  const cloudImgRef = useRef(null);
  const cloudOffsetRef = useRef(0);
  const planePosRef = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const crashRafRef = useRef(null);

  const [planePos, setPlanePos] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Betting state - LEFT
  const [leftBetAmount, setLeftBetAmount] = useState(1000);
  const [leftBetErrorMessage, setLeftBetErrorMessage] = useState({});
  const [leftBetButtonShow, setLeftBetButtonShow] = useState(true);
  const [leftBetArray, setLeftBetArray] = useState([]);
  const leftBetArrayRef = useRef([]);
  const [leftMainBetId, setLeftMainBetId] = useState("");
  const [leftCashOutAmount, setLeftCashOutAmount] = useState("");
  const [leftShowCashoutButton, setLeftShowCashoutButton] = useState(false);
  const [leftIsCashingOut, setLeftIsCashingOut] = useState(false);
  const leftBetPlacedRef = useRef(false);
  const leftBetAmountRef = useRef(leftBetAmount);
  const leftIsCashingOutRef = useRef(false);
  const [leftBetGameId, setLeftBetGameId] = useState("");

  // Betting state - RIGHT
  const [rightBetAmount, setRightBetAmount] = useState(1000);
  const [rightBetErrorMessage, setRightBetErrorMessage] = useState({});
  const [rightBetButtonShow, setRightBetButtonShow] = useState(true);
  const [rightBetArray, setRightBetArray] = useState([]);
  const rightBetArrayRef = useRef([]);
  const [rightMainBetId, setRightMainBetId] = useState("");
  const [rightCashOutAmount, setRightCashOutAmount] = useState("");
  const [rightShowCashoutButton, setRightShowCashoutButton] = useState(false);
  const [rightIsCashingOut, setRightIsCashingOut] = useState(false);
  const rightBetPlacedRef = useRef(false);
  const rightBetAmountRef = useRef(rightBetAmount);
  const rightIsCashingOutRef = useRef(false);
  const lostToastEvtRef = useRef(null);
  const pendingLostToastEvtRef = useRef(null);
  const [rightBetGameId, setRightBetGameId] = useState("");

  // Auto Bet / Auto Cashout
  const [leftAutoBet, setLeftAutoBet] = useState(false);
  const [rightAutoBet, setRightAutoBet] = useState(false);
  const [leftAutoCashout, setLeftAutoCashout] = useState(false);
  const [rightAutoCashout, setRightAutoCashout] = useState(false);
  const [leftAutoCashoutAt, setLeftAutoCashoutAt] = useState(1.1);
  const [rightAutoCashoutAt, setRightAutoCashoutAt] = useState(1.1);
  leftBetAmountRef.current = leftBetAmount;
  rightBetAmountRef.current = rightBetAmount;
  leftIsCashingOutRef.current = leftIsCashingOut;
  rightIsCashingOutRef.current = rightIsCashingOut;

  const showLostToastForUncashedBets = (evtId = roundIdRef.current) => {
    if (evtId && lostToastEvtRef.current === evtId) return;
    let lostAmount = 0;
    if (leftBetPlacedRef.current && !leftIsCashingOutRef.current) {
      lostAmount += Number(leftBetAmountRef.current) || 0;
    }
    if (rightBetPlacedRef.current && !rightIsCashingOutRef.current) {
      lostAmount += Number(rightBetAmountRef.current) || 0;
    }
    if (lostAmount <= 0) return;
    if (evtId) lostToastEvtRef.current = evtId;
    pendingLostToastEvtRef.current = null;
    showLostToast(lostAmount);
  };

  const isPostCrashScene = () => {
    const s = sceneRef.current;
    return s === "crashed" || s === "waiting" || s === "finished";
  };

  const leftAutoBetRef = useRef(false);
  const rightAutoBetRef = useRef(false);
  const leftAutoCashoutRef = useRef(false);
  const rightAutoCashoutRef = useRef(false);
  const leftAutoCashoutAtRef = useRef(1.1);
  const rightAutoCashoutAtRef = useRef(1.1);

  const [roundId, setRoundId] = useState("");
  const [resultsHistory, setResultsHistory] = useState([
    1.15, 2.34, 1.02, 3.87, 1.76, 5.12, 1.41, 2.08, 1.93, 4.55
  ]);
  const resultList = resultsHistory;

  // The PHP feed does not publish a history list, so keep the crashes we saw.
  const recordCrashInHistory = useCallback((value) => {
    const v = parseFloat(value);
    if (!Number.isFinite(v) || v <= 0) return;
    setResultsHistory((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.length > 0 && parseFloat(list[list.length - 1]) === v)
        return list;
      return [...list, v].slice(-10);
    });
  }, []);

  // Forget everything the ticker learned about the round that just ended.
  const resetRoundCounterState = useCallback(() => {
    crashKnownRef.current = false;
    lastServerRunningRef.current = 1;
    startTsRef.current = null;
    counterRawRef.current = null;
    capTsRef.current = 0;
    capFromRef.current = 1;
    capIntervalRef.current = SERVER_TICK_MS;
    flightValueRef.current = 0;
  }, []);

  // Reset to waiting
  const resetToWaiting = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (closedResetRef.current) {
      clearTimeout(closedResetRef.current);
      closedResetRef.current = null;
    }
    if (crashRafRef.current) cancelAnimationFrame(crashRafRef.current);
    setRunning(false);
    setFlightValue(0);
    maxFlightRef.current = null;
    resetRoundCounterState();
    lastFlightStartedEvtIdRef.current = null;
    setLeftBetButtonShow(true);
    setRightBetButtonShow(true);
    lastFinishedEvtIdRef.current = null;
    isLoadGameRef.current = 0;
    setScene("waiting");
    sceneRef.current = "waiting";
    const cvs = canvasRef.current;
    if (cvs) {
      const ctx = cvs.getContext("2d");
      ctx.clearRect(0, 0, cvs.width, cvs.height);
    }
    const resetPos = { x: 0, y: 0, w: 0, h: 0 };
    planePosRef.current = resetPos;
    setPlanePos(resetPos);
  }, [resetRoundCounterState]);

  // Background animation (stars + clouds)
  useEffect(() => {
    const cvs = bgCanvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width;
    const H = cvs.height;

    // Init stars
    if (starsRef.current.length === 0) {
      const stars = [];
      for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r: Math.random() * 1.8 + 0.3,
          alpha: Math.random() * 0.7 + 0.3,
          twinkleSpeed: Math.random() * 0.02 + 0.005,
          phase: Math.random() * Math.PI * 2
        });
      }
      starsRef.current = stars;
    }

    // Load cloud image
    if (!cloudImgRef.current) {
      const img = new Image();
      img.src = CLOUDS_SVG;
      cloudImgRef.current = img;
    }

    let frame = 0;
    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, W, H);

      // Stars
      for (const s of starsRef.current) {
        const a = s.alpha + Math.sin(frame * s.twinkleSpeed + s.phase) * 0.3;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(
          0.05,
          Math.min(1, a)
        )})`;
        ctx.fill();
      }

      // Scrolling clouds
      const cImg = cloudImgRef.current;
      if (cImg && cImg.complete && cImg.naturalWidth > 0) {
        const cloudW = W; // each tile = full canvas width for seamless wrap
        const cloudH = H * 0.42; // taller clouds covering bottom ~42%
        const cy = H - cloudH;
        cloudOffsetRef.current -= 0.6;
        if (Math.abs(cloudOffsetRef.current) >= cloudW)
          cloudOffsetRef.current = 0;
        for (let cx = cloudOffsetRef.current; cx < W + cloudW; cx += cloudW) {
          ctx.drawImage(cImg, cx, cy, cloudW, cloudH);
        }
      }

      bgRafRef.current = requestAnimationFrame(draw);
    };

    bgRafRef.current = requestAnimationFrame(draw);
    return () => {
      if (bgRafRef.current) cancelAnimationFrame(bgRafRef.current);
    };
  }, []);

  // Socket
  const { data, isConnected } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "luckyjet_galaxy"
  });

  console.log("data:", data);
  // ===== Socket reconnect handling =====
  useEffect(() => {
    if (!isConnected) {
      wasDisconnectedRef.current = true;
      // Pause the flight ticker while disconnected
      setRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    // Socket just came back online
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false;
      console.log("Socket reconnected \u2013 resetting LuckyJet state");

      // Reset the game to the loading state so server data drives the next scene
      setFlightValue(0);
      setScene("loading");
      sceneRef.current = "loading";
      isLoadGameRef.current = 0;
      lastCrashedEvtIdRef.current = null;
      lastFinishedEvtIdRef.current = null;
      lastFlightStartedEvtIdRef.current = null;
      maxFlightRef.current = null;
      resetRoundCounterState();
      const resetPos = { x: 0, y: 0, w: 0, h: 0 };
      planePosRef.current = resetPos;
      setPlanePos(resetPos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  const payloadResult = data?.data?.result;

  //console.log("data", data);
  const rawInner = data?.data;
  const payload = rawInner?.data?.evt_id
    ? rawInner.data
    : rawInner?.evt_id
    ? rawInner
    : null;

  // Derive lucky players directly from socket data (no state needed)
  const luckyPlayersRaw = rawInner?.data?.evt_id
    ? rawInner?.lucky_players
    : data?.lucky_players;
  const luckyPlayersList = (() => {
    if (!luckyPlayersRaw || typeof luckyPlayersRaw !== "object") return [];
    try {
      return Object.values(luckyPlayersRaw).map((entry) => {
        const obj = typeof entry === "string" ? JSON.parse(entry) : entry;
        const name = obj.uname || "";
        const masked =
          name.length > 2
            ? name.slice(0, 2) + "*".repeat(name.length - 2)
            : name;
        return {
          uname: masked,
          amount: obj.lockamt || obj.delivery || 0,
          rate: obj.rate || "-",
          profit:
            obj.rate && obj.lockamt
              ? (parseFloat(obj.lockamt) * parseFloat(obj.rate)).toFixed(2)
              : "-"
        };
      });
    } catch {
      return [];
    }
  })();

  const updateUserData = async (userData) => {
    await dispatch(setUserData(userData));
  };

  /**
   * Take off for the current round. The crash is not on the live feed, so the
   * counter starts from running_result and is capped by it every frame.
   */
  const beginFlight = (runningResult, { force = false } = {}) => {
    const evtId = roundIdRef.current;
    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (!force && evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (!force && evtId && lastFlightStartedEvtIdRef.current === evtId) return;
    if (!force && sceneRef.current === "crashed") return;

    lastFlightStartedEvtIdRef.current = evtId;
    crashKnownRef.current = false;

    const running = parseFloat(runningResult);
    const startValue = Number.isFinite(running) && running >= 1 ? running : 1;
    lastServerRunningRef.current = startValue;
    maxFlightRef.current = startValue;

    const isLateJoin = startValue > 1.15;
    capTsRef.current = performance.now();
    capFromRef.current = isLateJoin ? startValue : 1;
    capIntervalRef.current = SERVER_TICK_MS;

    if (isLateJoin) {
      // Joining a jet that is already up: land on the server value straight away.
      counterRawRef.current = startValue;
      flightValueRef.current = startValue;
      setFlightValue(+startValue.toFixed(2));
      lastStepRef.current = multiplierToStep(startValue, startValue);
      startTsRef.current =
        performance.now() - elapsedMsFromMultiplier(startValue);
    } else {
      counterRawRef.current = 1;
      flightValueRef.current = 1;
      setFlightValue(1);
      lastStepRef.current = -1;
      startTsRef.current = null;
    }

    setScene("flight");
    sceneRef.current = "flight";
    setRunning(true);
  };

  // Sync payload -> state
  useEffect(() => {
    if (!payload) return;
    updateUserData(data?.user);
    if (payload.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
      return;

    if (payload.evt_id) {
      setRoundId(payload.evt_id);
      roundIdRef.current = payload.evt_id;
    }
    if (
      Array.isArray(payload.results_history) &&
      payload.results_history.length > 0
    )
      setResultsHistory(payload.results_history);

    const evtStatus = payload.evt_status ?? payload.status;

    if (payload.start == 0) {
      const incomingEvtId = payload.evt_id;
      const isNewCountdownRound =
        incomingEvtId && incomingEvtId !== lastCountdownResetEvtRef.current;

      // Next evt can open while this client is still flying. Close that flight
      // and show lost before the new countdown clears the placed-bet flags.
      if (isNewCountdownRound && sceneRef.current === "flight") {
        const flyingEvtId = lastFlightStartedEvtIdRef.current;
        if (flyingEvtId && flyingEvtId !== incomingEvtId) {
          lastFinishedEvtIdRef.current = flyingEvtId;
          lastCrashedEvtIdRef.current = flyingEvtId;
          recordCrashInHistory(maxFlightRef.current);
          pendingLostToastEvtRef.current = flyingEvtId;
          showLostToastForUncashedBets(flyingEvtId);
        }
        setRunning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }

      if (lastCrashedEvtIdRef.current === payload.evt_id) {
        lastCrashedEvtIdRef.current = null;
      }
      lastFlightStartedEvtIdRef.current = null;
      resetRoundCounterState();
      if (closedResetRef.current) {
        clearTimeout(closedResetRef.current);
        closedResetRef.current = null;
      }

      // Transition from loading/waiting to countdown on new evt_id
      if (
        sceneRef.current === "loading" ||
        sceneRef.current === "waiting" ||
        sceneRef.current === "finished"
      ) {
        setLaunchCountdown(4);
        setFlightValue(0);
        setScene("countdown");
        sceneRef.current = "countdown";
      }

      if (isNewCountdownRound) {
        lastCountdownResetEvtRef.current = incomingEvtId;
        lostToastEvtRef.current = null;
        setLeftBetButtonShow(true);
        setLeftShowCashoutButton(false);
        setLeftMainBetId("");
        setLeftCashOutAmount("");
        leftBetPlacedRef.current = false;
        setLeftBetGameId("");
        if (leftBetArrayRef.current.length === 0) setLeftBetErrorMessage({});
        setRightBetButtonShow(true);
        setRightShowCashoutButton(false);
        setRightMainBetId("");
        setRightCashOutAmount("");
        rightBetPlacedRef.current = false;
        setRightBetGameId("");
        if (rightBetArrayRef.current.length === 0) setRightBetErrorMessage({});

        if (
          leftAutoBetRef.current &&
          leftBetArrayRef.current.length === 0 &&
          !leftBetPlacedRef.current
        )
          handleBetClick("left");
        if (
          rightAutoBetRef.current &&
          rightBetArrayRef.current.length === 0 &&
          !rightBetPlacedRef.current
        )
          handleBetClick("right");

        const flushLeft =
          leftBetArrayRef.current.length > 0 && !leftBetPlacedRef.current;
        const flushRight =
          rightBetArrayRef.current.length > 0 && !rightBetPlacedRef.current;
        if (flushLeft) {
          setLeftBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushRight) {
          setRightBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushLeft || flushRight) {
          placeAllBetsNowRef.current?.(flushLeft, flushRight);
        }
      }
    } else if (payload.start == 1 && evtStatus !== "CLOSED") {
      // The crash is masked while the jet is alive, so takeoff is driven by
      // start == 1 alone — payload.result stays "" until the round is CLOSED.
      // Mid-round load: join the jet already in the air from running_result.
      if (
        (sceneRef.current === "loading" || sceneRef.current === "waiting") &&
        lastFlightStartedEvtIdRef.current !== payload.evt_id
      ) {
        beginFlight(payload.running_result);
      }

      if (leftBetArrayRef.current.length > 0 || leftBetPlacedRef.current)
        setLeftBetButtonShow(false);
      if (rightBetArrayRef.current.length > 0 || rightBetPlacedRef.current)
        setRightBetButtonShow(false);

      isLoadGameRef.current = 1;

      if (leftBetPlacedRef.current && leftMainBetId)
        setLeftShowCashoutButton(true);
      if (rightBetPlacedRef.current && rightMainBetId)
        setRightShowCashoutButton(true);
    }
    if (payload.start == 0) isLoadGameRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  // Keep sceneRef in sync
  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    flightValueRef.current = flightValue;
  }, [flightValue]);

  /** running_result is a hard ceiling: each new value opens a fresh window for
   * the counter to animate into, so it moves smoothly without ever passing it. */
  useEffect(() => {
    if (!payload || payload.start != 1) return;
    const evtStatus = payload.evt_status ?? payload.status;
    if (evtStatus === "CLOSED") return;
    const srv = parseFloat(payload.running_result);
    if (!Number.isFinite(srv) || srv < 1) return;

    const nowTs = performance.now();
    const shown = Number.isFinite(counterRawRef.current)
      ? counterRawRef.current
      : 1;
    if (capTsRef.current > 0 && srv > lastServerRunningRef.current + 0.0001) {
      const measured = nowTs - capTsRef.current;
      if (measured >= 20 && measured <= 1500) {
        capIntervalRef.current = measured;
      }
    }
    capTsRef.current = nowTs;
    //added by uk - do not jump straight to the new server number.
    //added by uk - start from the number already on screen and climb up to it. Same filler as Aviator.
    capFromRef.current = Math.min(shown, srv);
    lastServerRunningRef.current = srv;
    maxFlightRef.current = srv;
  }, [
    payload?.running_result,
    payload?.start,
    payload?.evt_status,
    payload?.evt_id
  ]);

  // CLOSED + public result -> crash. This is the only thing that kills the jet.
  useEffect(() => {
    const status = payload?.evt_status ?? payload?.status;
    const evtId = payload?.evt_id;
    if (
      status === "CLOSED" &&
      evtId &&
      lastCrashedEvtIdRef.current !== evtId &&
      (sceneRef.current === "flight" || sceneRef.current === "countdown")
    ) {
      const result = parseFloat(payload?.result);
      if (!Number.isFinite(result) || result <= 0) return; // wait until result is public

      lastCrashedEvtIdRef.current = evtId;
      crashKnownRef.current = true;
      maxFlightRef.current = result;
      lastServerRunningRef.current = Math.max(
        lastServerRunningRef.current || 1,
        result
      );

      const shown = Number.isFinite(counterRawRef.current)
        ? counterRawRef.current
        : 1;
      const alreadyShowingCrash = shown + 0.001 >= result;
      const stillOnCountdown = sceneRef.current === "countdown";

      if (stillOnCountdown) {
        beginFlight(1, { force: true });
        crashKnownRef.current = true;
        maxFlightRef.current = result;
        lastServerRunningRef.current = Math.max(1, result);
        capFromRef.current = 1;
        capTsRef.current = performance.now();
      } else if (alreadyShowingCrash || sceneRef.current === "flight") {
        //added by uk - when the round closes, show the crash right away.
        counterRawRef.current = result;
        flightValueRef.current = result;
        lastStepRef.current = result;
        setFlightValue(result);
        triggerCrash();
      } else {
        capFromRef.current = shown;
        capTsRef.current = performance.now();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload?.evt_status,
    payload?.status,
    payload?.evt_id,
    payload?.result
  ]);

  // Countdown
  useEffect(() => {
    if (scene !== "countdown") return;

    // Follow the server's takeoff even if the local countdown still has seconds
    // left. Short rounds are over in a second and cannot wait for it.
    if (payload?.start == 1) {
      if (payload?.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
        return;
      const evtStatus = payload?.evt_status ?? payload?.status;
      if (evtStatus === "CLOSED") return;
      if (lastCrashedEvtIdRef.current === payload?.evt_id) return;
      if (lastFlightStartedEvtIdRef.current === payload?.evt_id) return;

      beginFlight(payload?.running_result);
      return;
    }

    if (launchCountdown <= 0) return; // wait for the server to start the round

    const t = setTimeout(() => setLaunchCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scene,
    launchCountdown,
    payload?.start,
    payload?.running_result,
    payload?.evt_id
  ]);

  // Flight ticker
  useEffect(() => {
    if (scene !== "flight" || !running) return;
    let countingStarted = false;

    const tick = (now) => {
      const evtId = roundIdRef.current;
      if (evtId && lastFinishedEvtIdRef.current === evtId) {
        setRunning(false);
        return;
      }

      const target = maxFlightRef.current;
      if (target == null || target <= 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (!countingStarted) {
        countingStarted = true;
        if (startTsRef.current == null) startTsRef.current = now;
        if (lastStepRef.current < 0) lastStepRef.current = 0;
      }

      const elapsed = Math.max(0, now - (startTsRef.current || now));
      const cap = lastServerRunningRef.current;
      const safeCap = Number.isFinite(cap) && cap >= 1 ? cap : 1;

      const from = Math.max(
        1,
        Number.isFinite(capFromRef.current) ? capFromRef.current : safeCap
      );
      const sinceCap = Math.max(0, now - (capTsRef.current || now));
      //added by uk - filler. Walk one cent at a time up to the server number.
      //added by uk - 200ms per cent until 1.10, then 91ms. Same speed as Aviator.
      const cents = Math.max(0, Math.round((safeCap - from) * 100));
      const stepMs = safeCap <= 1.1 ? 200 : 91;
      const catchWindowMs = Math.max(40, cents * stepMs);
      const progress = Math.min(1, sinceCap / catchWindowMs);
      let raw = from + (safeCap - from) * progress;
      raw = Math.min(safeCap, raw);

      const prevRaw = counterRawRef.current;
      if (Number.isFinite(prevRaw) && prevRaw > raw) raw = Math.min(prevRaw, safeCap);

      counterRawRef.current = raw;
      const next = Math.max(1, Math.floor(raw * 100) / 100);
      flightValueRef.current = next;

      const climbProgress = Math.min(1, elapsed / CLIMB_DURATION);
      const easedClimb = 1 - Math.pow(1 - climbProgress, 3);

      // Hold at running_result until CLOSED publishes the crash. The cap alone
      // never kills the jet.
      if (crashKnownRef.current && next + 0.001 >= (target || safeCap)) {
        const crashAt = target || safeCap;
        lastStepRef.current = crashAt;
        flightValueRef.current = crashAt;
        setFlightValue(crashAt);
        drawFlightCanvas(crashAt, crashAt, easedClimb, elapsed);
        triggerCrash();
        return;
      }

      if (next !== lastStepRef.current) {
        lastStepRef.current = next;
        setFlightValue(next);
        drawFlightCanvas(next, Math.max(safeCap, next), easedClimb, elapsed);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, running]);

  // Draw flight path curve
  const drawFlightCanvas = useCallback(
    (value, target, climbProgress, elapsed) => {
      const cvs = canvasRef.current;
      if (!cvs) return;
      const ctx = cvs.getContext("2d");
      const W = cvs.width;
      const H = cvs.height;
      ctx.clearRect(0, 0, W, H);

      const startX = W * 0.0;
      const stopX = W * 0.8;
      const startY = H;
      const endYTarget = H * 0.12;

      let tipX = startX + (stopX - startX) * climbProgress;
      let tipY = startY - (startY - endYTarget) * Math.pow(climbProgress, 2);

      // Sway at top
      if (climbProgress >= 1) {
        const amp = H * 0.08;
        const speed = 0.0015;
        const oscY = amp * Math.sin(speed * elapsed);
        const oscX = (oscY < 0 ? -1 : 1) * Math.abs(oscY * 1.2);
        tipX += oscX;
        tipY += oscY;
      }

      const GIF_H = 118;
      tipY = Math.max(GIF_H, tipY);
      tipX = Math.max(0, Math.min(W, tipX));

      const controlX = (startX + tipX) / 2;
      const controlY = H + 2;

      // Fill
      const grad = ctx.createLinearGradient(0, H, 0, 0);
      grad.addColorStop(0, "rgba(128, 0, 128, 0.18)");
      grad.addColorStop(1, "rgba(155, 90, 255, 0.25)");
      ctx.beginPath();
      ctx.moveTo(0, H);
      ctx.quadraticCurveTo(controlX, controlY, tipX, tipY);
      ctx.lineTo(tipX, H);
      ctx.lineTo(0, H);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // Stroke
      ctx.beginPath();
      ctx.moveTo(0, H);
      ctx.quadraticCurveTo(controlX, controlY, tipX, tipY);
      ctx.strokeStyle = "rgba(170, 100, 255, 0.85)";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Tip dot
      ctx.beginPath();
      ctx.arc(tipX, tipY, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.shadowColor = "#9b5aff";
      ctx.shadowBlur = 12;
      ctx.fill();
      ctx.shadowBlur = 0;

      const pos = { x: tipX, y: tipY, w: 118, h: 7 };
      planePosRef.current = pos;
      setPlanePos(pos);
    },
    []
  );

  // Crash
  const triggerCrash = () => {
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const crashedEvtId =
      lastFlightStartedEvtIdRef.current || roundIdRef.current;
    if (crashedEvtId) lastFinishedEvtIdRef.current = crashedEvtId;
    recordCrashInHistory(maxFlightRef.current);
    pendingLostToastEvtRef.current = crashedEvtId;
    showLostToastForUncashedBets(crashedEvtId);
    setScene("crashed");
    sceneRef.current = "crashed";
    if (closedResetRef.current) clearTimeout(closedResetRef.current);
    closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);
  };

  // Crash fly-off animation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (scene !== "crashed") {
      if (crashRafRef.current) cancelAnimationFrame(crashRafRef.current);
      return;
    }
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dX = cvs.width * 0.015;
    const dY = cvs.height * 0.015;

    const fly = () => {
      const p = planePosRef.current;
      const nx = p.x + dX;
      const ny = p.y - dY;
      if (nx > cvs.width + 200 || ny < -200) {
        const hidden = { x: -999, y: -999, w: 0, h: 0 };
        planePosRef.current = hidden;
        setPlanePos(hidden);
        return;
      }
      const next = { ...p, x: nx, y: ny };
      planePosRef.current = next;
      setPlanePos(next);
      crashRafRef.current = requestAnimationFrame(fly);
    };

    crashRafRef.current = requestAnimationFrame(fly);
    return () => {
      if (crashRafRef.current) cancelAnimationFrame(crashRafRef.current);
    };
  }, [scene]);

  // Bet click
  const handleBetClick = (side) => {
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;
    if (!betAmount || betAmount <= 0) return;
    const liveOdds = Number(payload?.b1);
    const betObj = {
      amount: Number(betAmount),
      profit: 0,
      catmid: payload?.evt_id || roundIdRef.current || roundId,
      round: payload?.evt_id || roundIdRef.current || roundId,
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      teamname: "luckyjet",
      bettype: "b",
      marketVal: 1,
      game_type: 10,
      market_type: "m",
      game_name: "luckyjet"
    };
    betArrayRef.current = [
      ...betArrayRef.current,
      { ...betObj, is_bet: false }
    ];
    setBetArray([...betArrayRef.current]);
    setBetErrorMessage({ status: true, message: "Placing bet..." });
    placeAllBetsNowRef.current?.(side === "left", side === "right");
  };

  // Place all queued bets
  const placeAllBetsNow = async (wantLeft, wantRight) => {
    //added by uk - send the right bet now. Do not wait for the left request, or it stays on "Placing bet...".
    const hasLeft = !!wantLeft && !leftPlacingRef.current;
    const hasRight = !!wantRight && !rightPlacingRef.current;
    if (!hasLeft && !hasRight) return;
    const combinedBetArray = [];
    let leftBetObj = null;
    let rightBetObj = null;

    if (hasLeft) {
      const bets = [...leftBetArrayRef.current];
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      for (const bet of bets) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = roundIdRef.current || bet.catmid;
          bet.round = roundIdRef.current || bet.round;
          leftBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 0
          });
        }
      }
    }
    if (hasRight) {
      const bets = [...rightBetArrayRef.current];
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      for (const bet of bets) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = roundIdRef.current || bet.catmid;
          bet.round = roundIdRef.current || bet.round;
          rightBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 1
          });
        }
      }
    }
    if (combinedBetArray.length === 0) return;
    if (hasLeft) leftPlacingRef.current = true;
    if (hasRight) rightPlacingRef.current = true;

    const restoreQueuedBets = () => {
      if (hasLeft && leftBetObj) {
        leftBetArrayRef.current = [{ ...leftBetObj, is_bet: false }];
        setLeftBetArray(leftBetArrayRef.current);
      }
      if (hasRight && rightBetObj) {
        rightBetArrayRef.current = [{ ...rightBetObj, is_bet: false }];
        setRightBetArray(rightBetArrayRef.current);
      }
    };

    const baseBet = leftBetObj || rightBetObj;
    const liveOdds = Number(baseBet?.odds);
    const requestBody = {
      ...baseBet,
      amount: Number(baseBet?.amount),
      teamname: "luckyjet",
      bettype: baseBet?.bettype || "b",
      market_type: "m",
      game_name: "luckyjet",
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      catmid: roundIdRef.current || baseBet.catmid,
      round: roundIdRef.current || baseBet.round,
      bet_array: combinedBetArray
    };

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino",
        requestBody
      );
      if (response.data?.status != null) {
        if (response.data.status) {
          const returnBets = response.data?.data?.return_bets;
          const betsArray = Array.isArray(returnBets)
            ? returnBets
            : returnBets?.bet_id
            ? [returnBets]
            : [];
          if (hasLeft && hasRight) {
            if (betsArray[0]?.bet_id) {
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
            }
            if (betsArray[1]?.bet_id) {
              setRightMainBetId(betsArray[1].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
            }
          } else if (hasLeft) {
            if (betsArray[0]?.bet_id) {
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
            }
          } else if (hasRight) {
            if (betsArray[0]?.bet_id) {
              setRightMainBetId(betsArray[0].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
            }
          }
          const anyBetConfirmed =
            (hasLeft && leftBetPlacedRef.current) ||
            (hasRight && rightBetPlacedRef.current);
          const msg = { status: true, message: response.data.message };
          if (hasLeft) setLeftBetErrorMessage(msg);
          if (hasRight) setRightBetErrorMessage(msg);
          if (anyBetConfirmed) {
            const placedEvtId = roundIdRef.current;
            const roundAlreadyOver =
              pendingLostToastEvtRef.current === placedEvtId ||
              lastFinishedEvtIdRef.current === placedEvtId;
            if (roundAlreadyOver) {
              showLostToastForUncashedBets(placedEvtId);
            }
          }
        } else {
          restoreQueuedBets();
          const tooLate = /time is up/i.test(response.data.message || "");
          const msg = {
            status: tooLate,
            message: tooLate
              ? "Previous round running. Bet queued"
              : response.data.message
          };
          if (hasLeft) setLeftBetErrorMessage(msg);
          if (hasRight) setRightBetErrorMessage(msg);
        }
      }
    } catch (error) {
      restoreQueuedBets();
      const errMsg = {
        status: false,
        message: error.response?.data?.message || "Bet placement failed"
      };
      if (hasLeft) setLeftBetErrorMessage(errMsg);
      if (hasRight) setRightBetErrorMessage(errMsg);
    } finally {
      if (hasLeft) leftPlacingRef.current = false;
      if (hasRight) rightPlacingRef.current = false;
    }
  };
  placeAllBetsNowRef.current = placeAllBetsNow;

  // Cancel bet
  const cancelBet = (side) => {
    if (side === "left") {
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      setLeftBetErrorMessage({ status: false, message: "Bet cancelled" });
    } else {
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      setRightBetErrorMessage({ status: false, message: "Bet cancelled" });
    }
  };

  /** Highest multiplier the server has published for this round so far. */
  const publishedCap = () => {
    const cap = lastServerRunningRef.current;
    return Number.isFinite(cap) && cap >= 1 ? cap : null;
  };

  // Cashout
  const cashOutNow = async (side, multiplierOverride) => {
    const mainBetId = side === "left" ? leftMainBetId : rightMainBetId;
    const isCashingOut = side === "left" ? leftIsCashingOut : rightIsCashingOut;
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const setIsCashingOut =
      side === "left" ? setLeftIsCashingOut : setRightIsCashingOut;
    const setShowCashoutButton =
      side === "left" ? setLeftShowCashoutButton : setRightShowCashoutButton;
    const setMainBetId = side === "left" ? setLeftMainBetId : setRightMainBetId;
    const betPlacedRef = side === "left" ? leftBetPlacedRef : rightBetPlacedRef;
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const setCashOutAmount =
      side === "left" ? setLeftCashOutAmount : setRightCashOutAmount;
    const setBetButtonShow =
      side === "left" ? setLeftBetButtonShow : setRightBetButtonShow;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;

    if (!mainBetId || isCashingOut) return;

    // Never claim more than the server has published, and never below 1.10.
    const rawMultiplier =
      multiplierOverride != null && Number(multiplierOverride) > 0
        ? Number(multiplierOverride)
        : Number(flightValueRef.current);
    const cap = publishedCap();
    const claim = cap == null ? rawMultiplier : Math.min(rawMultiplier, cap);
    if (!(claim + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;

    const incrementor = claim.toFixed(2);
    const betGameId = side === "left" ? leftBetGameId : rightBetGameId;
    const gameId = betGameId || roundId;

    setIsCashingOut(true);
    if (side === "left") leftIsCashingOutRef.current = true;
    else rightIsCashingOutRef.current = true;
    setShowCashoutButton(false);

    try {
      const response = await axiosInstance.post("/casino/cash-out", {
        game_id: gameId,
        bet_id: mainBetId,
        gametype: "luckyjet",
        win_multiplier: incrementor
      });
      const result = response.data;
      if (result.isSuccess && result.error === 0) {
        const amt =
          result.data?.cash_out_amount ??
          result.cash_out_amount ??
          betAmount * parseFloat(incrementor);
        if (amt != null && amt !== "") {
          showWinToast(amt);
        }
        setBetErrorMessage({});
        setShowCashoutButton(false);
        setBetButtonShow(true);
        setMainBetId("");
        setCashOutAmount("");
        betPlacedRef.current = false;
        betArrayRef.current = [];
        setBetArray([]);
      } else {
        const crashAt = Number(maxFlightRef.current);
        const requested = parseFloat(incrementor);
        const cashedAtOrBelowCrash =
          Number.isFinite(crashAt) &&
          Number.isFinite(requested) &&
          requested <= crashAt + 0.001;
        if (isPostCrashScene() && !cashedAtOrBelowCrash) {
          if (side === "left") leftIsCashingOutRef.current = false;
          else rightIsCashingOutRef.current = false;
          showLostToastForUncashedBets();
        }
        setShowCashoutButton(sceneRef.current === "flight");
        setBetErrorMessage({
          status: false,
          message: result.message || "Cashout failed"
        });
      }
    } catch (error) {
      const crashAt = Number(maxFlightRef.current);
      const requested = parseFloat(incrementor);
      const cashedAtOrBelowCrash =
        Number.isFinite(crashAt) &&
        Number.isFinite(requested) &&
        requested <= crashAt + 0.001;
      if (isPostCrashScene() && !cashedAtOrBelowCrash) {
        if (side === "left") leftIsCashingOutRef.current = false;
        else rightIsCashingOutRef.current = false;
        showLostToastForUncashedBets();
      }
      setShowCashoutButton(sceneRef.current === "flight");
      setBetErrorMessage({
        status: false,
        message: error.response?.data?.message || "Cashout request failed"
      });
    } finally {
      if (side === "left") leftIsCashingOutRef.current = false;
      else rightIsCashingOutRef.current = false;
      setIsCashingOut(false);
    }
  };

  // Re-sync bets on crash
  useEffect(() => {
    if (scene === "crashed") {
      setLeftBetArray([...leftBetArrayRef.current]);
      setRightBetArray([...rightBetArrayRef.current]);
    }
  }, [scene]);

  // Show cashout during flight
  useEffect(() => {
    if (scene === "flight") {
      if (leftBetPlacedRef.current && leftMainBetId)
        setLeftShowCashoutButton(true);
      if (rightBetPlacedRef.current && rightMainBetId)
        setRightShowCashoutButton(true);
    }
  }, [scene, leftMainBetId, rightMainBetId]);

  // Auto cashout
  useEffect(() => {
    if (scene !== "flight" || flightValue + 0.0001 < CASHOUT_MIN_MULTIPLIER)
      return;
    const cap = publishedCap();
    const claim = cap == null ? flightValue : Math.min(flightValue, cap);
    if (claim + 0.0001 < CASHOUT_MIN_MULTIPLIER) return;
    if (
      leftAutoCashoutRef.current &&
      leftShowCashoutButton &&
      !leftIsCashingOut
    ) {
      const t = parseFloat(leftAutoCashoutAtRef.current);
      if (t > 0 && flightValue + 0.0001 >= t) cashOutNow("left", claim);
    }
    if (
      rightAutoCashoutRef.current &&
      rightShowCashoutButton &&
      !rightIsCashingOut
    ) {
      const t = parseFloat(rightAutoCashoutAtRef.current);
      if (t > 0 && flightValue + 0.0001 >= t) cashOutNow("right", claim);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftIsCashingOut,
    rightIsCashingOut
  ]);

  // Update cashout amount
  useEffect(() => {
    if (scene === "flight" && flightValue > 0) {
      if (leftShowCashoutButton && leftBetAmount > 0)
        setLeftCashOutAmount(
          (parseFloat(leftBetAmount) * flightValue).toFixed(2)
        );
      if (rightShowCashoutButton && rightBetAmount > 0)
        setRightCashOutAmount(
          (parseFloat(rightBetAmount) * flightValue).toFixed(2)
        );
    }
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftBetAmount,
    rightBetAmount
  ]);

  const hasValidPayload = payload != null && !!payload?.evt_id;

  // Bet panel
  const renderBetBox = (side) => {
    const isLeft = side === "left";
    const betAmount = isLeft ? leftBetAmount : rightBetAmount;
    const setBetAmount = isLeft ? setLeftBetAmount : setRightBetAmount;
    const betArray = isLeft ? leftBetArray : rightBetArray;
    const showCashoutButton = isLeft
      ? leftShowCashoutButton
      : rightShowCashoutButton;
    const betButtonShow = isLeft ? leftBetButtonShow : rightBetButtonShow;
    const cashOutAmount = isLeft ? leftCashOutAmount : rightCashOutAmount;
    const isCashingOut = isLeft ? leftIsCashingOut : rightIsCashingOut;
    const betErrorMessageObj = isLeft
      ? leftBetErrorMessage
      : rightBetErrorMessage;
    const autoBet = isLeft ? leftAutoBet : rightAutoBet;
    const autoCashout = isLeft ? leftAutoCashout : rightAutoCashout;
    const autoCashoutAt = isLeft ? leftAutoCashoutAt : rightAutoCashoutAt;

    const setAutoBetState = (val) => {
      if (isLeft) {
        setLeftAutoBet(val);
        leftAutoBetRef.current = val;
      } else {
        setRightAutoBet(val);
        rightAutoBetRef.current = val;
      }
    };
    const setAutoCashoutState = (val) => {
      if (isLeft) {
        setLeftAutoCashout(val);
        leftAutoCashoutRef.current = val;
      } else {
        setRightAutoCashout(val);
        rightAutoCashoutRef.current = val;
      }
    };
    const setAutoCashoutAtState = (val) => {
      if (isLeft) {
        setLeftAutoCashoutAt(val);
        leftAutoCashoutAtRef.current = val;
      } else {
        setRightAutoCashoutAt(val);
        rightAutoCashoutAtRef.current = val;
      }
    };

    const showCancel = betArray.length > 0 && !showCashoutButton;
    const showCashout =
      showCashoutButton &&
      scene === "flight" &&
      flightValue + 0.0001 >= CASHOUT_MIN_MULTIPLIER;
    const showBet = !showCancel && !showCashout;

    return (
      <div className="RBC-Jetx-BetBox">
        <div className="RBC-Jetx-TopRow">
          <div className="RBC-Jetx-BetColumn">
            <div className="RBC-Jetx-LabelContainer">
              <label>Bet</label>
              <div className="RBC-Jetx-Toggle">
                <span>AUTO</span>
                <label className="RBC-Jetx-Switch">
                  <input
                    type="checkbox"
                    checked={autoBet}
                    onChange={(e) => setAutoBetState(e.target.checked)}
                  />
                  <span className="RBC-Jetx-Slider"></span>
                </label>
              </div>
            </div>
            <div className="RBC-Jetx-InputContainer">
              <button
                className="RBC-Jetx-InputBtn"
                onClick={() => setBetAmount((v) => Math.max(1, v - 100))}
              >
                -
              </button>
              <input
                type="text"
                className="RBC-Jetx-Input"
                value={betAmount}
                onInput={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setBetAmount(
                    val === "" ? "" : Math.max(1, parseInt(val) || 0)
                  );
                }}
              />
              <button
                className="RBC-Jetx-InputBtn"
                onClick={() => setBetAmount((v) => v + 100)}
              >
                +
              </button>
            </div>
          </div>

          <div className="RBC-Jetx-BetColumn">
            <div className="RBC-Jetx-LabelContainer">
              <label>Cash</label>
              <div className="RBC-Jetx-Toggle">
                <span>AUTO</span>
                <label className="RBC-Jetx-Switch">
                  <input
                    type="checkbox"
                    checked={autoCashout}
                    onChange={(e) => setAutoCashoutState(e.target.checked)}
                  />
                  <span className="RBC-Jetx-Slider"></span>
                </label>
              </div>
            </div>
            <div className="RBC-Jetx-InputContainer">
              <button
                className="RBC-Jetx-InputBtn"
                onClick={() =>
                  setAutoCashoutAtState(
                    Math.max(1.1, +(autoCashoutAt - 0.02).toFixed(2))
                  )
                }
              >
                -
              </button>
              <input
                type="text"
                className="RBC-Jetx-Input"
                value={autoCashoutAt}
                onChange={(e) =>
                  setAutoCashoutAtState(parseFloat(e.target.value) || 1.1)
                }
              />
              <button
                className="RBC-Jetx-InputBtn"
                onClick={() =>
                  setAutoCashoutAtState(+(autoCashoutAt + 0.02).toFixed(2))
                }
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className="RBC-Jetx-QuickBet">
          <button
            className="RBC-Jetx-QuickBetBtn"
            onClick={() => setBetAmount(2000)}
          >
            2000
          </button>
          <button
            className="RBC-Jetx-QuickBetBtn"
            onClick={() => setBetAmount(5000)}
          >
            5000
          </button>
          <button
            className="RBC-Jetx-QuickBetAll"
            onClick={() => setBetAmount((v) => v + 1000)}
          >
            +1000
          </button>
          <button
            className="RBC-Jetx-QuickBetAll"
            onClick={() => setBetAmount((v) => v + 5000)}
          >
            +5000
          </button>
        </div>

        {showBet && (
          <button
            className="RBC-Jetx-BetBtn mt-1 btn btn-success"
            onClick={() => handleBetClick(side)}
            disabled={!betButtonShow || !hasValidPayload}
          >
            {scene === "countdown" && hasValidPayload ? "Place" : "Next"}
          </button>
        )}
        {showCancel && (
          <button
            className="RBC-Jetx-BetBtn mt-1 btn btn-info"
            onClick={() => cancelBet(side)}
          >
            Cancel
          </button>
        )}
        {showCashout && (
          <button
            className="RBC-Jetx-BetBtn mt-1 btn btn-warning"
            onClick={() => cashOutNow(side)}
            disabled={isCashingOut}
          >
            {"Cashout" + (cashOutAmount ? " " + cashOutAmount : "")}
          </button>
        )}

        {betErrorMessageObj.message && (
          <div
            className={
              "alert " +
              (betErrorMessageObj.status ? "alert-success" : "alert-danger") +
              " m-0 p-1 mt-1"
            }
            style={{ fontSize: "11px" }}
          >
            {betErrorMessageObj.message}
          </div>
        )}
      </div>
    );
  };

  // Render
  return (
    <GalaxyLayout>
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={false}
        closeButton={false}
        newestOnTop
        limit={3}
      />
      <style>{`
        .av-win-toast,
        .av-lost-toast {
          border-radius: 8px !important;
          padding: 12px 20px !important;
          min-height: unset !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.38) !important;
          overflow: hidden;
        }
        .av-win-toast { background: #2d7a3a !important; }
        .av-lost-toast { background: #c62828 !important; }
        .av-win-toast .Toastify__toast-body,
        .av-lost-toast .Toastify__toast-body {
          padding: 0 !important;
          margin: 0 !important;
        }
        .av-win-toast .Toastify__progress-bar,
        .av-lost-toast .Toastify__progress-bar {
          background: rgba(0, 0, 0, 0.2) !important;
          height: 4px;
        }
        .av-toast-body {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #ffffff;
          font-size: 18px;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
        }
        .av-toast-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
        }
        .lj-tabs-fix.mobile-Tab {
          flex-wrap: nowrap !important;
          overflow-x: hidden !important;
          width: 100%;
        }
        .lj-tabs-fix .nav-item {
          flex: 1 1 0;
          min-width: 0 !important;
        }
        .lj-tabs-fix .nav-link {
          min-width: 0 !important;
          white-space: nowrap;
          padding: 4px 8px;
          font-size: 13px;
          text-align: center;
          width: 100%;
        }
      `}</style>
      <div className="container mt-4" id="rbc-app">
        <div>
          <div className="tabs-section">
            <ul className="nav nav-tabs mobile-Tab nav-tabs-glxy lj-tabs-fix">
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link active" to="/galaxy-casino/1">
                  <span className="tab-icon">&#127918;</span> All Games{" "}
                  <span className="badge bg-warning text-dark">6</span>
                </Link>
              </li>
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link" to="/galaxy-casino/2">
                  <span className="tab-icon">&#127918;</span> Crash{" "}
                  <span className="badge bg-warning text-dark">1</span>
                </Link>
              </li>
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link" to="/galaxy-casino/3">
                  <span className="tab-icon">&#127918;</span> Card{" "}
                  <span className="badge bg-warning text-dark">5</span>
                </Link>
              </li>
            </ul>
            <ul className="nav nav-tabs desktop-tab nav-tabs-glxy">
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link active" to="/galaxy-casino/1">
                  <span className="tab-icon">&#127918;</span> All Games{" "}
                  <span className="badge bg-warning text-dark">6</span>
                </Link>
              </li>
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link" to="/galaxy-casino/2">
                  <span className="tab-icon">&#127918;</span> Crash{" "}
                  <span className="badge bg-warning text-dark">1</span>
                </Link>
              </li>
              <li className="nav-item rbc-games-tab">
                <Link className="nav-link" to="/galaxy-casino/3">
                  <span className="tab-icon">&#127918;</span> Card{" "}
                  <span className="badge bg-warning text-dark">5</span>
                </Link>
              </li>
            </ul>
          </div>

          <div id="rbc-game-view">
            <div className="container-fluid RBC-Jetx-MainContainer">
              <div className="RBC-Jetx-LeftPanel" id="lastresults">
                {resultsHistory && resultsHistory.length > 0 ? (
                  resultsHistory.map((val, idx) => {
                    const v = parseFloat(val);
                    return (
                      <div
                        key={idx}
                        className={
                          "RBC-Jetx-Multiplier " +
                          (v >= 2 ? "RBC-Jetx-Green" : "RBC-Jetx-Red")
                        }
                      >
                        {v.toFixed(2)}
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="RBC-Jetx-Multiplier"
                    style={{ color: "#888" }}
                  >
                    <div className="RBC-Jetx-LeftPanel">
                      {Object.keys(resultList).map((idx) => (
                        <div
                          key={idx}
                          className={
                            resultList[idx] < 2
                              ? "RBC-Jetx-Multiplier RBC-Jetx-Red"
                              : "RBC-Jetx-Multiplier RBC-Jetx-Green"
                          }
                        >
                          {" "}
                          {resultList[idx]}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="RBC-Jetx-CenterPanel">
                <div
                  className="RBC-Jetx-GameScreen B-height scroll-containeraviator-galaxy"
                  style={{
                    background:
                      "linear-gradient(rgb(52, 5, 11), rgb(2, 17, 30))",
                    overflow: "hidden"
                  }}
                >
                  <canvas
                    ref={bgCanvasRef}
                    width={843}
                    height={390}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 0
                    }}
                  />

                  <canvas
                    ref={canvasRef}
                    id="animationCanvas"
                    width={843}
                    height={390}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 1
                    }}
                  />

                  {(scene === "flight" || scene === "crashed") &&
                    planePos.w > 0 && (
                      <img
                        id="planeGif"
                        src={BOY_GIF}
                        alt="Lucky Jet"
                        style={{
                          position: "absolute",
                          width: (planePos.w / 843) * 100 + "%",
                          height: "auto",
                          pointerEvents: "none",
                          display: "block",
                          left:
                            ((planePos.x - planePos.w / 2) / 843) * 100 + "%",
                          top:
                            (Math.max(0, planePos.y - planePos.w * 0.85) /
                              390) *
                              100 +
                            "%",
                          transition:
                            scene === "crashed"
                              ? "none"
                              : "left 0.12s ease-out, top 0.12s ease-out",
                          zIndex: 2
                        }}
                      />
                    )}

                  {/* Overlay: disconnected */}
                  {!isConnected && (
                    <div
                      className="overlayaviatorx-galaxy"
                      style={{ zIndex: 999, background: "rgba(0,0,0,0.85)" }}
                    >
                      <div
                        className="overlay-contentx-galaxy"
                        style={{ flexDirection: "column" }}
                      >
                        <span
                          className="overlay-textaviatorx-glaxy"
                          style={{ color: "#ff4444" }}
                        >
                          <b>Connection Lost</b>
                        </span>
                        <span
                          className="overlay-textaviatorx-glaxy"
                          style={{ fontSize: "14px", color: "#ccc" }}
                        >
                          Reconnecting...
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Overlay: loading — only until the first socket payload arrives */}
                  {isConnected && scene === "loading" && (
                    <div className="overlayaviatorx-galaxy">
                      <div className="overlay-contentx-galaxy">
                        <img src={LOADER_SVG} alt="Loading" />
                        <span className="overlay-textaviatorx-glaxy">
                          <b>Loading...</b>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Overlay: waiting */}
                  {isConnected && scene === "waiting" && (
                    <div className="overlayaviatorx-galaxy">
                      <div className="overlay-contentx-galaxy">
                        <img src={LOADER_SVG} alt="Loading" />
                        <span className="overlay-textaviatorx-glaxy">
                          <b>Waiting for next round...</b>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Overlay: countdown */}
                  {isConnected && scene === "countdown" && (
                    <div className="overlayaviatorx-galaxy">
                      <div className="overlay-contentx-galaxy">
                        <img src={LOADER_SVG} alt="Loading" />
                        <span className="overlay-textaviatorx-glaxy">
                          <b>STARTING NEXT ROUND IN {launchCountdown}</b>
                        </span>
                      </div>
                    </div>
                  )}

                  {scene === "flight" && flightValue >= 1 && (
                    <div className="Odds_Text_JetX">
                      <div>x {flightValue.toFixed(2)}</div>
                    </div>
                  )}

                  {scene === "crashed" && flightValue > 0 && (
                    <div
                      className="Odds_Text_JetX"
                      style={{ color: "#ff4747" }}
                    >
                      <div>x {flightValue.toFixed(2)}</div>
                      <span
                        style={{
                          display: "block",
                          fontSize: "24px",
                          color: "#ff4747",
                          fontWeight: "bold"
                        }}
                      >
                        FLEW AWAY!
                      </span>
                    </div>
                  )}

                  <div className="waveStrength-3">
                    <div
                      className="wv4 wave"
                      style={{
                        borderTopColor: payload
                          ? "rgb(0,255,0)"
                          : "rgb(192,192,192)"
                      }}
                    >
                      <div className="wv3 wave">
                        <div className="wv2 wave">
                          <div className="wv1 wave"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="round-id-container">
                    {"LuckyJet " + (roundId || "--")}
                    <span className="info-icon">&#9432;</span>
                  </div>

                  <div className="Connectedclients-glxy mt-1">
                    <span className="user-icon"></span>{" "}
                    <b>
                      <span className="number">{luckyPlayersList.length}</span>
                    </b>
                  </div>
                </div>

                <div className="RBC-Jetx-BetSection">
                  {renderBetBox("left")}
                  {renderBetBox("right")}
                </div>
              </div>

              <div className="RBC-Jetx-RightPanel">
                <table className="RBC-Jetx-Table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Size</th>
                      <th>Bet</th>
                      <th>Collect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {luckyPlayersList.length > 0 ? (
                      luckyPlayersList.map((bet, idx) => {
                        const hasCollected = bet.rate && bet.rate !== "-";
                        return (
                          <tr
                            key={idx}
                            className={hasCollected ? "highlight" : ""}
                          >
                            <td className="user-column">
                              <span
                                className="user-badge"
                                style={{
                                  backgroundColor: hasCollected
                                    ? "#2bc46b"
                                    : "#8a47ff"
                                }}
                              >
                                {(bet.uname || "?")[0].toUpperCase()}
                              </span>
                              {bet.uname}
                            </td>
                            <td className="size-column">{bet.amount}</td>
                            <td
                              className={
                                "bet-column " +
                                (hasCollected ? "highlight-bet" : "")
                              }
                            >
                              {hasCollected ? bet.rate : "--"}
                            </td>
                            <td
                              className={
                                "collect-column " +
                                (hasCollected ? "highlight-collect" : "")
                              }
                            >
                              {hasCollected ? bet.profit : ""}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          style={{
                            textAlign: "center",
                            color: "#888"
                          }}
                        >
                          No bets yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GalaxyLayout>
  );
}
